import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  getExpandedRowModel,
  flexRender,
  getSortedRowModel,
} from '@tanstack/react-table'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { Button, ButtonText } from '../ui/button'
import {
  CheckIcon,
  ChevronDownIcon,
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  StopIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/16/solid'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import useGetMoneyPaths from '@/services/api/money-path/useGetMoneyPaths'
// import DeleteMoneyPathModal from './DeleteMoneyPathModal'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import useGetTodos from '@/services/api/to-do/useGetTodos'
import { formatDate } from 'date-fns'
import useNumberFormatter from '@/hooks/useNumberFormatter'
import DeleteButton from './DeleteButton'
import ConfirmButton from './ConfirmButton'
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip'
import useAppStore from '@/store/useAppStore'

const columnHelper = createColumnHelper()

function ColumnHeader({ children, className, ...rest }) {
  return (
    <div
      className={cn(['tw-font-medium tw-text-primary px-4'], className)}
      {...rest}
    >
      {children}
    </div>
  )
}

function Actions({ row }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  return (
    <Button
      className="relative"
      iconOnly
      variant="ghost"
      onClick={() => setOpen(!open)}
    >
      <EllipsisHorizontalIcon />
      {open && (
        <>
          <div className="bg-red-500">another</div>
          <div className="absolute bottom-0 right-0 translate-y-full transform">
            hello
          </div>
        </>
      )}
      {/* <div>Hello there</div> */}
    </Button>
  )
}

export default function MyTodoTransactionsTable({
  toVault,
  firstLevelRowId,
  secondLevelRowId,
}: {
  // transactions: Array<any>
  toVault: {
    fromVault: number
    to_vault: number
    to_vault_name: string
    total: number
    transactions: Array<any>
  }
  firstLevelRowId: number | string
  secondLevelRowId: number | string
}) {
  const formatter = useNumberFormatter({
    // style: 'currency',
    // currency: 'AUD',
    // locale: 'ja-JP',
  })

  const transactions = toVault.transactions

  const navigate = useNavigate()

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: ({ table }) => (
          <ColumnHeader
            className={`pl-16--- group flex h-full w-auto items-center bg-gray-low   py-2  text-txt-secondary`}
          >
            Transactions
          </ColumnHeader>
        ),
        enableSorting: true,
        cell: ({ row, getValue }) => (
          <div className="flex w-full items-center justify-between">
            <span>{row.original.name}</span>
          </div>
        ),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('amount', {
        header: ({ table }) => (
          <ColumnHeader
            className={`flex h-full items-center justify-end py-2 text-txt-secondary`}
          >
            Amount
          </ColumnHeader>
        ),
        enableSorting: true,
        cell: ({ row, getValue }) => (
          <div className="inline-grid justify-end gap-1 text-right">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    title="Edit"
                    onClick={() => updateSelectedRow(row)}
                    preventScrollReset={true}
                    to={`/vaults/${toVault.fromVault}/transactions/${row.original.id}/edit?type=transfer&editSeries=false`}
                  >
                    <span className="cursor-pointer  group-hover/row:underline">
                      {formatter(getValue())}
                    </span>
                  </Link>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent align="end">
                    Edit transfer
                    <TooltipArrow />
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </TooltipProvider>
          </div>
        ),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('transaction_date', {
        header: ({ table }) => (
          <ColumnHeader
            className={` inline-grid h-full items-center justify-end py-2 text-txt-secondary`}
          >
            Date
          </ColumnHeader>
        ),
        enableSorting: true,
        cell: ({ row, getValue }) => <div className="">{getValue()}</div>,
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('source.amount', {
        header: ({ table }) => (
          <ColumnHeader
            className={`inline-grid  h-full w-full items-center justify-end py-2 text-txt-secondary`}
          >
            Source Amount
          </ColumnHeader>
        ),
        enableSorting: true,
        cell: ({ row, getValue }) => (
          // <div className="justify-end text-right">{formatter(getValue())}</div>
          <div className="inline-grid justify-end gap-1 text-right">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    title="Edit"
                    onClick={() => updateSelectedRow(row)}
                    preventScrollReset={true}
                    to={`/categories/${row.original.source.transaction_category_id}/transactions/${row.original.source.id}/edit?type=in&editSeries=false`}
                  >
                    <span className="cursor-pointer  group-hover/row:underline">
                      {formatter(getValue())}
                    </span>
                  </Link>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent align="end">
                    Edit source transaction
                    <TooltipArrow />
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </TooltipProvider>
          </div>
        ),
        footer: (info) => info.column.id,
      }),

      columnHelper.accessor('source.description', {
        header: ({ table }) => (
          <ColumnHeader
            className={` flex h-full items-center justify-end py-2 text-txt-secondary`}
          >
            Source Description
          </ColumnHeader>
        ),
        enableSorting: true,
        cell: ({ row, getValue }) => <div className="">{getValue()}</div>,
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('action', {
        header: ({ table }) => (
          <ColumnHeader
            className={` flex h-full items-center justify-end py-2 text-txt-secondary`}
          >
            {null}
          </ColumnHeader>
        ),
        enableSorting: false,
        cell: ({ row, getValue }) => (
          <div className="flex items-center justify-end">
            <ConfirmButton id={row.original.id} />
            <DeleteButton id={row.original.id} />
            {/* <Link
              title="Edit"
              preventScrollReset={true}
              to={`/categories/${row.original.source.transaction_category_id}/transactions/${row.original.source.id}/edit?type=in&editSeries=false&offset=-1`}
            >
              <Button iconOnly variant="ghost">
                <PencilSquareIcon />
              </Button>
            </Link> */}

            {/* <DeleteMoneyPathModal id={row.original.id} /> */}
          </div>
        ),
        footer: (info) => info.column.id,
      }),
    ],
    [],
  )

  const { data, status } = useGetMoneyPaths()

  const table = useReactTable({
    data: transactions,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getSubRows: (row) => row.toVaults,
  })

  const [searchParams, setSearchParams] = useSearchParams()
  const _fromVault = searchParams.get('from-vault')
  const _toVault = searchParams.get('to-vault')
  const _transactionRow = searchParams.get('transaction-row')

  const [transactionRow, setTransactionRow] = useState(_transactionRow)

  const selectedMyTodoTransactionRowId = useAppStore(
    (state) =>
      state.selectedMyTodoTransactionRowId ??
      _fromVault + '-' + _toVault + '-' + _transactionRow,
  )
  const setSelectedMyTodoTransactionRowId = useAppStore(
    (state) => state.setSelectedMyTodoTransactionRowId,
  )

  function updateSelectedRow(row) {
    const currentUrl = new URL(window.location.href)
    const newParams = new URLSearchParams(currentUrl.search)
    newParams.set('transaction-row', row.id)
    newParams.set('from-vault', firstLevelRowId)
    newParams.set('to-vault', secondLevelRowId)

    window.history.replaceState(null, '', '?' + newParams.toString())
    setSelectedMyTodoTransactionRowId(
      firstLevelRowId + '-' + secondLevelRowId + '-' + row.id,
    )
  }

  if (status === 'pending') return 'Loading....'

  return (
    <div
      className={cn(
        'rounded-md-- col-span-full grid grid-cols-subgrid overflow-auto border border-b-0 border-t-0 transition-all data-[busy]:pointer-events-none data-[busy]:animate-pulse data-[busy]:opacity-50',
      )}
    >
      <div className="col-span-full grid grid-cols-subgrid overflow-auto   [&>div:not(:last-child)]:border-b">
        {table.getHeaderGroups().map((headerGroup) => (
          <div
            key={headerGroup.id}
            className="col-span-full grid h-[60px] grid-cols-subgrid bg-gray-low [&>div:not(:last-child)]:border-r [&>div]:border-solid"
          >
            {headerGroup.headers.map((header, i) => {
              return (
                <div
                  key={header.id}
                  className={`border-none`}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </div>
              )
            })}
          </div>
        ))}
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => {
            return (
              <>
                {/* row */}
                <div
                  id={firstLevelRowId + '-' + secondLevelRowId + '-' + row.id}
                  key={row.id}
                  data-active={
                    selectedMyTodoTransactionRowId ==
                    firstLevelRowId + '-' + secondLevelRowId + '-' + row.id
                      ? true
                      : undefined
                  }
                  onClick={() => {
                    updateSelectedRow(row)
                  }}
                  className={`group/row relative col-span-full grid h-[52px] grid-cols-subgrid !opacity-100 data-[active]:bg-slate-100 data-[active]:outline data-[active]:outline-border [&>div:not(:last-child)]:border-r [&>div:not(:last-child)]:border-solid`}
                >
                  {row.getVisibleCells().map((cell) => (
                    // cell
                    <div
                      key={cell.id}
                      className={`group/cell relative flex items-center border-none px-4 transition-all group-hover/row:bg-opacity-15 [&>div]:flex [&>div]:h-full [&>div]:w-full [&>div]:items-center `}
                      data-first-col={
                        cell.column.getIsFirstColumn() ? true : undefined
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </div>
                  ))}
                </div>
              </>
            )
          })
        ) : (
          <div>
            <div className=" text-center">No results.</div>
          </div>
        )}
      </div>
    </div>
  )
}
